import React, { useState, useEffect } from 'react';
import api from '../../api';
import { Briefcase, Plus, CheckCircle, Search, Trash2, X, Pencil } from 'lucide-react';
import UIElementGuard from '../UIElementGuard';
import ConfirmModal from '../ConfirmModal';

const TpoManagement = ({ isDeactivatedView = false }) => {
  const [tpos, setTpos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newTpo, setNewTpo] = useState({
    full_name: '',
    email: '',
    tpo_id: '',
    designation: '',
    department: '',
    phone: '',
    password: ''
  });
  const [addingTpo, setAddingTpo] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [permissionError, setPermissionError] = useState('');
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTpo, setEditingTpo] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', tpo_id: '', designation: '', department: '', phone_number: '' });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: null
  });
  const closeConfirmModal = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  useEffect(() => {
    fetchTpos();
  }, [isDeactivatedView]);

  const handleToggleStatus = async (tpoMember) => {
    const newActive = !tpoMember.is_active;
    const newStatus = newActive ? 'Active' : 'Deactivated';
    const actionText = newActive ? 'activate' : 'deactivate';
    
    setConfirmModal({
      isOpen: true,
      title: `Confirm ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`,
      message: `Are you sure you want to ${actionText} ${tpoMember.full_name || 'this TPO member'}?`,
      type: newActive ? 'primary' : 'danger',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await api.put(`/university/users/${tpoMember.id}/status`, {
            is_active: newActive,
            status: newStatus
          });
          fetchTpos();
        } catch (err) {
          console.error("Failed to update TPO status", err);
          alert(err.response?.data?.detail || `Failed to ${actionText} TPO member`);
        }
      }
    });
  };

  const fetchTpos = async () => {
    setLoading(true);
    setPermissionError('');
    try {
      const res = await api.get(`/tpo?status=${isDeactivatedView ? 'deactivated' : 'active'}`);
      setTpos(res.data || []);
    } catch (err) {
      console.error("Failed to fetch TPOs", err);
      if (err.response && err.response.status === 403) {
        setPermissionError(err.response?.data?.detail || "You don't have permission to access this resource.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (tpoMember) => {
    setEditingTpo(tpoMember);
    setEditForm({
      full_name: tpoMember.full_name || '',
      tpo_id: tpoMember.tpo_id || '',
      designation: tpoMember.designation || '',
      department: tpoMember.department || '',
      phone_number: tpoMember.phone || ''
    });
    setEditModalOpen(true);
  };

  const handleUpdateTpo = async (e) => {
    e.preventDefault();
    if (editForm.phone_number && !/^\d{10}$/.test(editForm.phone_number.trim())) {
      alert("Phone number must contain exactly 10 digits.");
      return;
    }
    try {
      await api.put(`/tpo/${editingTpo.id}`, editForm);
      setEditModalOpen(false);
      fetchTpos();
      setSuccessMessage("TPO member updated successfully!");
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error("Failed to update TPO", err);
      alert(err.response?.data?.detail || "Failed to update TPO");
    }
  };

  const [fieldErrors, setFieldErrors] = useState({});
  const [modalError, setModalError] = useState('');

  const alphaPattern = /^[a-zA-Z\s.,'-]+$/;
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const getErrorDetail = (error) => {
    const detail = error.response?.data?.detail ?? error.message;

    if (typeof detail === 'string') {
      return detail;
    }

    if (Array.isArray(detail)) {
      return detail.map(item => {
        if (typeof item === 'string') return item;
        if (item?.msg) return item.msg;
        return JSON.stringify(item);
      }).join('; ');
    }

    if (detail && typeof detail === 'object') {
      return detail.message || detail.msg || JSON.stringify(detail);
    }

    return 'An unexpected error occurred.';
  };

  const validateTpoForm = () => {
    const errors = {};

    if (!newTpo.full_name || !newTpo.full_name.trim()) {
      errors.full_name = "Full Name is required.";
    } else if (!alphaPattern.test(newTpo.full_name.trim())) {
      errors.full_name = "Name contains invalid characters.";
    }

    if (!newTpo.email || !newTpo.email.trim()) {
      errors.email = "Email is required.";
    } else if (!emailPattern.test(newTpo.email.trim())) {
      errors.email = "Please enter a valid email address.";
    }

    if (!newTpo.password || !newTpo.password.trim()) {
      errors.password = "Password is required.";
    } else if (newTpo.password.trim().length < 8) {
      errors.password = "Password must be at least 8 characters long.";
    }

    if (newTpo.department && newTpo.department.trim()) {
      if (!alphaPattern.test(newTpo.department.trim())) {
        errors.department = "Department contains invalid characters.";
      }
    }

    if (newTpo.phone && newTpo.phone.trim()) {
      const phoneStr = newTpo.phone.trim();
      if (!/^\d{10}$/.test(phoneStr)) {
        errors.phone = "Phone number must contain exactly 10 digits.";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid = () => {
    if (!newTpo.full_name || !newTpo.full_name.trim() || !alphaPattern.test(newTpo.full_name.trim())) {
      return false;
    }

    if (!newTpo.email || !newTpo.email.trim() || !emailPattern.test(newTpo.email.trim())) {
      return false;
    }

    if (!newTpo.password || !newTpo.password.trim() || newTpo.password.trim().length < 8) {
      return false;
    }

    if (newTpo.department && newTpo.department.trim() && !alphaPattern.test(newTpo.department.trim())) {
      return false;
    }

    if (newTpo.phone && newTpo.phone.trim() && !/^\d{10}$/.test(newTpo.phone.trim())) {
      return false;
    }

    return true;
  };

  const handleOpenAddModal = () => {
    setModalError('');
    setFieldErrors({});
    setNewTpo({
      full_name: '',
      email: '',
      tpo_id: '',
      designation: '',
      department: '',
      phone: '',
      password: ''
    });
    setAddModalOpen(true);
  };

  const handleAddTpo = async (e) => {
    e.preventDefault();
    if (!validateTpoForm()) {
      setModalError("Please fix the validation errors highlighted below.");
      return;
    }
    setAddingTpo(true);
    setModalError('');
    try {
      await api.post('/tpo', newTpo);
      setSuccessMessage('TPO account created successfully!');
      setAddModalOpen(false);
      setNewTpo({
        full_name: '',
        email: '',
        tpo_id: '',
        designation: '',
        department: '',
        phone: '',
        password: ''
      });
      setFieldErrors({});
      fetchTpos();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      const detail = getErrorDetail(err);
      setModalError('Failed to create TPO: ' + detail);
      const normalizedDetail = detail.toLowerCase();
      if (normalizedDetail.includes('email') || normalizedDetail.includes('registered')) {
        setFieldErrors(prev => ({ ...prev, email: detail }));
      }
    } finally {
      setAddingTpo(false);
    }
  };

  const handleDeleteTpo = async (tpoId, name) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete TPO Officer',
      message: `Are you sure you want to delete TPO Officer "${name}"?`,
      type: 'danger',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await api.delete(`/tpo/${tpoId}`);
          setTpos(prev => prev.filter(t => t.id !== tpoId));
        } catch (err) {
          alert("Failed to delete TPO member: " + (err.response?.data?.detail || err.message));
        }
      }
    });
  };

  const filteredTpos = tpos.filter(t => {
    const term = searchTerm.toLowerCase();
    return (
      (t.full_name && t.full_name.toLowerCase().includes(term)) ||
      (t.email && t.email.toLowerCase().includes(term)) ||
      (t.tpo_id && t.tpo_id.toLowerCase().includes(term)) ||
      (t.designation && t.designation.toLowerCase().includes(term)) ||
      (t.department && t.department.toLowerCase().includes(term))
    );
  });

  return (
    <div style={{ padding: '20px 0' }}>
      {successMessage && (
        <div style={{
          background: '#d1fae5',
          color: '#065f46',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <CheckCircle size={20} />
          {successMessage}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-main, #1e293b)', margin: '0 0 4px 0' }}>
            {isDeactivatedView ? 'Deactivated TPOs' : 'Training & Placement Officers (TPO)'}
          </h2>
          <p style={{ color: 'var(--text-muted, #64748b)', margin: 0, fontSize: '0.95rem' }}>
            {isDeactivatedView ? 'View and reactivate deactivated TPO accounts.' : 'Manage placement team officers who can access the dedicated TPO portal.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {!isDeactivatedView && (
            <UIElementGuard page="tpo_management" element="add_tpo_btn">
              <button
                onClick={handleOpenAddModal}
                className="btn btn-primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  borderRadius: '8px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
                }}
              >
                <Plus size={16} /> Add TPO
              </button>
            </UIElementGuard>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '250px', maxWidth: '400px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Search TPOs by name, ID, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 38px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: 'var(--card-bg, #ffffff)',
              color: 'var(--text-main, #1e293b)',
              fontSize: '0.9rem'
            }}
          />
        </div>
        <div style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '500' }}>
          {filteredTpos.length} {filteredTpos.length === 1 ? 'Officer' : 'Officers'}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner" />
          <p style={{ color: '#64748b', marginTop: '10px' }}>Loading TPO Officers...</p>
        </div>
      ) : permissionError ? (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '12px', padding: '20px', textAlign: 'center', color: '#991b1b' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: '600' }}>Access Restricted</h4>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>{permissionError}</p>
        </div>
      ) : filteredTpos.length === 0 ? (
        <div style={{ background: 'var(--card-bg, #ffffff)', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
          <Briefcase size={40} style={{ color: '#94a3b8', marginBottom: '12px' }} />
          <h3 style={{ margin: '0 0 6px 0', fontSize: '1.1rem', color: '#1e293b' }}>
            {isDeactivatedView ? 'No Deactivated TPO Accounts' : 'No TPO Officers Found'}
          </h3>
          <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '0.9rem' }}>
            {isDeactivatedView ? 'All TPO accounts are currently active.' : 'Get started by adding your institution\'s placement officers.'}
          </p>
          {!isDeactivatedView && (
            <button
              onClick={handleOpenAddModal}
              className="btn btn-primary"
              style={{ padding: '8px 16px', borderRadius: '6px', background: '#3b82f6', color: 'white', border: 'none', fontWeight: '600', cursor: 'pointer' }}
            >
              Add TPO Officer
            </button>
          )}
        </div>
      ) : (
        <div style={{ background: 'var(--card-bg, #ffffff)', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>Name</th>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>TPO ID</th>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>Designation</th>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>Department</th>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>Email</th>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>Phone</th>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTpos.map((tpo) => (
                <tr key={tpo.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '14px 16px', fontWeight: '600', color: '#0f172a' }}>{tpo.full_name || 'N/A'}</td>
                  <td style={{ padding: '14px 16px', color: '#64748b' }}>{tpo.tpo_id || 'N/A'}</td>
                  <td style={{ padding: '14px 16px', color: '#64748b' }}>{tpo.designation || 'N/A'}</td>
                  <td style={{ padding: '14px 16px', color: '#64748b' }}>{tpo.department || 'N/A'}</td>
                  <td style={{ padding: '14px 16px', color: '#3b82f6' }}>{tpo.email}</td>
                  <td style={{ padding: '14px 16px', color: '#64748b' }}>{tpo.phone || 'N/A'}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '0.78rem',
                      fontWeight: '700',
                      background: tpo.is_active ? '#dcfce7' : '#fee2e2',
                      color: tpo.is_active ? '#166534' : '#991b1b',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: tpo.is_active ? '#22c55e' : '#ef4444' }}></span>
                      {tpo.is_active ? 'Active' : 'Deactivated'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'right', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => handleToggleStatus(tpo)}
                      style={{
                        background: tpo.is_active ? '#fff1f2' : '#f0fdf4',
                        color: tpo.is_active ? '#e11d48' : '#16a34a',
                        border: `1px solid ${tpo.is_active ? '#fda4af' : '#86efac'}`,
                        cursor: 'pointer',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: '700',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title={tpo.is_active ? "Deactivate Account" : "Activate Account"}
                    >
                      {tpo.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    {!isDeactivatedView && (
                      <UIElementGuard page="tpo_management" element="edit_tpo_btn">
                        <button
                          onClick={() => handleOpenEdit(tpo)}
                          style={{
                            background: '#eff6ff',
                            color: '#3b82f6',
                            border: '1px solid #bfdbfe',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                          title="Edit TPO Member"
                        >
                          <Pencil size={17} />
                        </button>
                      </UIElementGuard>
                    )}
                    {!isDeactivatedView && (
                      <UIElementGuard page="tpo_management" element="delete_tpo_btn">
                        <button
                          onClick={() => handleDeleteTpo(tpo.id, tpo.full_name || tpo.name)}
                          style={{
                            background: '#fff1f2',
                            color: '#e11d48',
                            border: '1px solid #fda4af',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            marginLeft: '4px'
                          }}
                          title="Delete TPO Member"
                        >
                          <Trash2 size={17} />
                        </button>
                      </UIElementGuard>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add TPO Modal */}
      {addModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(3px)' }}>
          <div style={{ background: '#ffffff', color: '#1e293b', padding: '30px', borderRadius: '16px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.35rem', fontWeight: '700', color: '#0f172a' }}>Add TPO Officer</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.88rem', color: '#64748b' }}>Create a new TPO account for your institution.</p>
              </div>
              <button onClick={() => setAddModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '26px', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>
                <X size={24} />
              </button>
            </div>
            {modalError && (
              <div style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fca5a5', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '0.88rem' }}>
                {modalError}
              </div>
            )}
            <form onSubmit={handleAddTpo} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.9rem', fontWeight: '500', color: '#334155' }}>Name *</label>
                <input
                  type="text"
                  name="full_name"
                  autoComplete="off"
                  required
                  value={newTpo.full_name}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewTpo(prev => ({ ...prev, full_name: val }));
                    if (!val.trim()) {
                      setFieldErrors(prev => ({ ...prev, full_name: 'Full Name is required.' }));
                    } else if (!alphaPattern.test(val.trim())) {
                      setFieldErrors(prev => ({ ...prev, full_name: 'Name contains invalid characters.' }));
                    } else {
                      setFieldErrors(prev => ({ ...prev, full_name: null }));
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${fieldErrors.full_name ? '#ef4444' : '#cbd5e1'}`,
                    fontSize: '0.95rem',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.full_name && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{fieldErrors.full_name}</span>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.9rem', fontWeight: '500', color: '#334155' }}>Email *</label>
                <input
                  type="email"
                  name="tpo_email"
                  autoComplete="off"
                  required
                  value={newTpo.email}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewTpo(prev => ({ ...prev, email: val }));
                    if (!val.trim()) {
                      setFieldErrors(prev => ({ ...prev, email: 'Email is required.' }));
                    } else if (!emailPattern.test(val.trim())) {
                      setFieldErrors(prev => ({ ...prev, email: 'Please enter a valid email address.' }));
                    } else {
                      setFieldErrors(prev => ({ ...prev, email: null }));
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${fieldErrors.email ? '#ef4444' : '#cbd5e1'}`,
                    fontSize: '0.95rem',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.email && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{fieldErrors.email}</span>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.9rem', fontWeight: '500', color: '#334155' }}>TPO ID</label>
                <input
                  type="text"
                  name="tpo_id"
                  autoComplete="off"
                  value={newTpo.tpo_id}
                  onChange={(e) => setNewTpo(prev => ({ ...prev, tpo_id: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.95rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.9rem', fontWeight: '500', color: '#334155' }}>Designation</label>
                <input
                  type="text"
                  name="designation"
                  autoComplete="off"
                  placeholder="e.g. Head of Placement"
                  value={newTpo.designation}
                  onChange={(e) => setNewTpo(prev => ({ ...prev, designation: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.95rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.9rem', fontWeight: '500', color: '#334155' }}>Department</label>
                <input
                  type="text"
                  name="department"
                  autoComplete="off"
                  value={newTpo.department}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewTpo(prev => ({ ...prev, department: val }));
                    if (val.trim() && !alphaPattern.test(val.trim())) {
                      setFieldErrors(prev => ({ ...prev, department: 'Department contains invalid characters.' }));
                    } else {
                      setFieldErrors(prev => ({ ...prev, department: null }));
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${fieldErrors.department ? '#ef4444' : '#cbd5e1'}`,
                    fontSize: '0.95rem',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.department && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{fieldErrors.department}</span>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.9rem', fontWeight: '500', color: '#334155' }}>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  autoComplete="off"
                  value={newTpo.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setNewTpo(prev => ({ ...prev, phone: val }));
                    if (val.trim() && !/^\d{10}$/.test(val.trim())) {
                      setFieldErrors(prev => ({ ...prev, phone: 'Phone number must contain exactly 10 digits.' }));
                    } else {
                      setFieldErrors(prev => ({ ...prev, phone: null }));
                    }
                  }}
                  maxLength={10}
                  placeholder="e.g. 9876543210"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${fieldErrors.phone ? '#ef4444' : '#cbd5e1'}`,
                    fontSize: '0.95rem',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.phone && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{fieldErrors.phone}</span>}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.9rem', fontWeight: '500', color: '#334155' }}>Password *</label>
                <input
                  type="password"
                  name="new_password"
                  autoComplete="new-password"
                  required
                  value={newTpo.password}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewTpo(prev => ({ ...prev, password: val }));
                    if (!val.trim()) {
                      setFieldErrors(prev => ({ ...prev, password: 'Password is required.' }));
                    } else if (val.trim().length < 8) {
                      setFieldErrors(prev => ({ ...prev, password: 'Password must be at least 8 characters long.' }));
                    } else {
                      setFieldErrors(prev => ({ ...prev, password: null }));
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${fieldErrors.password ? '#ef4444' : '#cbd5e1'}`,
                    fontSize: '0.95rem',
                    boxSizing: 'border-box'
                  }}
                />
                {fieldErrors.password && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{fieldErrors.password}</span>}
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => setAddModalOpen(false)}
                  style={{ padding: '10px 18px', borderRadius: '8px', background: '#f1f5f9', border: 'none', color: '#475569', fontWeight: '600', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingTpo || !isFormValid()}
                  style={{
                    padding: '10px 22px',
                    borderRadius: '8px',
                    background: isFormValid() ? '#3b82f6' : '#94a3b8',
                    border: 'none',
                    color: 'white',
                    fontWeight: '600',
                    cursor: (addingTpo || !isFormValid()) ? 'not-allowed' : 'pointer',
                    boxShadow: isFormValid() ? '0 4px 10px rgba(59, 130, 246, 0.25)' : 'none',
                    opacity: (addingTpo || !isFormValid()) ? 0.6 : 1,
                    transition: 'all 0.2s ease'
                  }}
                >
                  {addingTpo ? 'Creating...' : 'Save TPO'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Modal */}
      {editModalOpen && editingTpo && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '500px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem' }}>Edit TPO Member</h3>
              <button onClick={() => setEditModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
            </div>
            
            <form onSubmit={handleUpdateTpo}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#475569' }}>Full Name</label>
                <input 
                  type="text" 
                  value={editForm.full_name} 
                  onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                  required
                />
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#475569' }}>TPO ID</label>
                <input 
                  type="text" 
                  value={editForm.tpo_id} 
                  onChange={(e) => setEditForm({...editForm, tpo_id: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#475569' }}>Designation</label>
                <input 
                  type="text" 
                  value={editForm.designation} 
                  onChange={(e) => setEditForm({...editForm, designation: e.target.value})}
                  placeholder="e.g. Head of Placement"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                />
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#475569' }}>Department</label>
                <input 
                  type="text" 
                  value={editForm.department} 
                  onChange={(e) => setEditForm({...editForm, department: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div style={{ marginBottom: '25px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', color: '#475569' }}>Phone Number</label>
                <input 
                  type="text" 
                  value={editForm.phone_number} 
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setEditForm({...editForm, phone_number: val});
                  }}
                  maxLength={10}
                  placeholder="e.g. 9876543210"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setEditModalOpen(false)} style={{ padding: '10px 16px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ padding: '10px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal 
        {...confirmModal} 
        onCancel={closeConfirmModal} 
      />
    </div>
  );
};

export default TpoManagement;
